import React, { useEffect } from 'react';
import { Row, Col, Modal, ModalHeader, ModalBody, Button, ModalFooter } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import FormInput from '../ReactHookFormCustom/FormInput';
import FormSelect from '../ReactHookFormCustom/FormSelect';
import { emailFormat } from '../ReactHookFormCustom/FormHelpers';
import { UserRole } from '../../Helpers/enums';
import { ModalMode } from '../../Helpers/enums';

const UserModal = ({
  showModal,
  onHandleValidUserSubmit,
  onHandleInValidUserSubmit,
  userToViewOrEdit,
  modalPurpose,
  closeModal,
}) => {
  const { t } = useTranslation();
  const { handleSubmit, control, reset, watch } = useForm({});
  const password = watch('password');

  useEffect(() => {
    reset({
      id: userToViewOrEdit?.id,
      userName: userToViewOrEdit?.username,
      firstName: userToViewOrEdit?.firstName,
      lastName: userToViewOrEdit?.lastName,
    });
  }, [userToViewOrEdit]);

  const userRoleOptions = Object.entries(UserRole).map(([value, label]) => ({
    value,
    label,
  }));

  const numberOfRoles = userToViewOrEdit?.roles?.length ?? 0;

  const defaultUserRole = userRoleOptions.find((option) =>
    numberOfRoles > 0 ? option.value === userToViewOrEdit?.roles[0] : option.value === UserRole.Editor
  );

  return (
    <div>
      <Modal isOpen={showModal} centered>
        <ModalHeader>
          <em className="fa-2x icon-user mr-3"> {t('common.USER')}</em>
        </ModalHeader>
        <form onSubmit={handleSubmit(onHandleValidUserSubmit, onHandleInValidUserSubmit)}>
          <ModalBody>
            <Row className="pb-3">
              <Col>
                <FormInput
                  name="userName"
                  id="userNameId"
                  control={control}
                  label={t('common.USERNAME')}
                  type="email"
                  disabled={modalPurpose === ModalMode.View}
                  helpMessage={'Must be a valid Email address. Invitation will be sent here.'}
                  rules={{
                    required: t('common.REQUIRED'),
                    pattern: {
                      value: emailFormat,
                      message: t('settings.EMAIL_ERROR'),
                    },
                  }}
                />
              </Col>
            </Row>
            <Row className="pb-3">
              <Col>
                <FormInput
                  name="firstName"
                  id="firstNameId"
                  control={control}
                  label={t('common.FIRST_NAME')}
                  type="text"
                  disabled={modalPurpose === ModalMode.View}
                  rules={{
                    required: t('common.REQUIRED'),
                  }}
                />
              </Col>
            </Row>
            <Row className="pb-3">
              <Col>
                <FormInput
                  name="lastName"
                  id="lastNameId"
                  control={control}
                  label={t('common.LAST_NAME')}
                  type="text"
                  disabled={modalPurpose === ModalMode.View}
                  rules={{
                    required: t('common.REQUIRED'),
                  }}
                />
                <FormInput
                  name="id"
                  control={control}
                  hidden
                  defaultValue={userToViewOrEdit?.id}
                  type="text"
                  disabled
                />
              </Col>
            </Row>
            <Row className="pb-3">
              <Col>
                <FormSelect
                  name="userRole"
                  id="userRoleId"
                  isDisabled={modalPurpose === ModalMode.View}
                  control={control}
                  label="User Role"
                  options={userRoleOptions}
                  defaultValue={defaultUserRole}
                  rules={{ required: t('common.REQUIRED') }}
                />
              </Col>
            </Row>
            {modalPurpose === ModalMode.Create && (
              <div>
                <Row className="pb-3">
                  <Col>
                    <FormInput
                      name="password"
                      type="password"
                      id="passwordId"
                      control={control}
                      label={t('common.PASSWORD')}
                      disabled={modalPurpose === ModalMode.View}
                      rules={{
                        required: t('common.REQUIRED'),
                      }}
                    />
                  </Col>
                </Row>
                <Row className="pb-3">
                  <Col>
                    <FormInput
                      name="password2"
                      id="password2Id"
                      type="password"
                      control={control}
                      label={t('common.RETYPE_PASSWORD')}
                      disabled={modalPurpose === ModalMode.View}
                      rules={{
                        required: t('common.REQUIRED'),
                        validate: (value) => value === password || t('common.RETYPE_PASSWORD_ERR_MISMATCH_MSG'),
                      }}
                    />
                  </Col>
                </Row>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            {(modalPurpose === ModalMode.Edit || modalPurpose === ModalMode.Create) && (
              <>
                <Button color="primary" type="submit">
                  {t('common.SAVE')}
                </Button>{' '}
                <Button color="secondary" onClick={closeModal}>
                  {t('common.CANCEL')}
                </Button>
              </>
            )}
            {modalPurpose === ModalMode.View && (
              <Button color="primary" onClick={closeModal}>
                {t('common.OK')}
              </Button>
            )}
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};

export default UserModal;
